import { createAction } from "@reduxjs/toolkit";
import type { IChatInfo } from "../chats-models";
import type {
  ICallInfo,
  ICallTipNotice,
  IGetFromLastIdMessagesConfig,
  IMessageInfo,
  IUpdateMessagesReadConfig,
} from "./messenger-models";

type ChatNote = IChatInfo["notes"][number];
type MessagePage = { data: IMessageInfo[]; next?: string };

export namespace IMessengerActions {
  export enum Type {
    GET__FETCH_CHAT__SUCCESS = "GET__FETCH_CHAT__SUCCESS",
    GET__FETCH_CHAT__RESET = "GET__FETCH_CHAT__RESET",
    GET__FETCH_CHAT__PREPARE = "GET__FETCH_CHAT__PREPARE",

    GET__FETCH_CHAT_RELOAD__SUCCESS = "GET__FETCH_CHAT_RELOAD__SUCCESS",

    POST__CREATE_CHAT__SUCCESS = "POST__CREATE_CHAT__SUCCESS",

    UPDATE__CHAT_BY_ID = "UPDATE__CHAT_BY_ID",

    GET__FETCH_MESSAGES__SUCCESS = "GET__FETCH_MESSAGES__SUCCESS",
    GET__FETCH_MESSAGES__RESET = "GET__FETCH_MESSAGES__RESET",
    GET__FETCH_NEXT_MESSAGES__SUCCESS = "GET__FETCH_NEXT_MESSAGES__SUCCESS",

    GET__MESSAGE_BY_ID__SUCCESS = "GET__MESSAGE_BY_ID__SUCCESS",

    GET__FROM_LAST_ID_MESSAGES__SUCCESS = "GET__FROM_LAST_ID_MESSAGES__SUCCESS",

    POST__SEND_MESSAGE__INIT = "POST__SEND_MESSAGE__INIT",
    POST__SEND_MESSAGE__SUCCESS = "POST__SEND_MESSAGE__SUCCESS",
    POST__SEND_MESSAGE__FAILURE = "POST__SEND_MESSAGE__FAILURE",

    PATCH__BUY_PAID_CONTENT__SUCCESS = "PATCH__BUY_PAID_CONTENT__SUCCESS",

    REMOVE__CLIENT_SIDE_MESSAGE = "REMOVE__CLIENT_SIDE_MESSAGE",

    ADD_CLIENT_SIDE_MESSAGE_RECEIVED = "ADD_CLIENT_SIDE_MESSAGE_RECEIVED",

    SET_CURRENT_ID = "SET_CURRENT_ID",

    // SET_FIRST_LOAD = 'SET_FIRST_LOAD',

    RESET__POST_SEND_MESSAGE = "RESET__POST_SEND_MESSAGE",

    UPDATE__MESSAGE_REVEAL_MEDIA = "UPDATE__MESSAGE_REVEAL_MEDIA",

    UPDATE__MESSAGE_AS_PAID = "UPDATE__MESSAGE_AS_PAID",

    UPDATE__MESSAGE_AS_HIDDEN = "UPDATE__MESSAGE_AS_HIDDEN",

    UPDATE__MESSAGE_AS_DELETED = "UPDATE__MESSAGE_AS_DELETED",

    UPDATE__MESSAGE = "UPDATE__MESSAGE",

    UPDATE__MESSAGE_AS_EXPIRED = "UPDATE__MESSAGE_AS_EXPIRED",

    UPDATE__MESSAGES_READ = "UPDATE__MESSAGES_READ",

    UPDATE__MESSAGES_DESTRUCTIBLE = "UPDATE__MESSAGES_DESTRUCTIBLE",

    UPDATE__SEND_MESSAGE_PROGRESS = "UPDATE__SEND_MESSAGE_PROGRESS",

    UPDATE__UNLOCK_BY_PAYING_USD = "UPDATE__UNLOCK_BY_PAYING_USD",

    POST__CHAT_NOTE__SUCCESS = "POST__CHAT_NOTE__SUCCESS",

    PATCH__CHAT_NOTE__SUCCESS = "PATCH__CHAT_NOTE__SUCCESS",

    DELETE__CHAT_NOTE__SUCCESS = "DELETE__CHAT_NOTE__SUCCESS",

    DELETE__MESSAGE__SUCCESS = "DELETE__MESSAGE__SUCCESS",

    PATCH__HIDE_MESSAGE__SUCCESS = "PATCH__HIDE_MESSAGE__SUCCESS",

    POST__MESSAGE_REACTION__SUCCESS = "POST__MESSAGE_REACTION__SUCCESS",

    DELETE__MESSAGE_REACTION__SUCCESS = "DELETE__MESSAGE_REACTION__SUCCESS",

    SET__NOTE_TEXT = "SET__NOTE_TEXT",
    SET__EDITING_NOTE_ID = "SET__EDITING_NOTE_ID",
    SET__NOTES_OPEN = "SET__NOTES_OPEN",

    GET__CHAT_GALLERY__SUCCESS = "GET__CHAT_GALLERY__SUCCESS",

    GET__CHAT_GALLERY_NEXT__SUCCESS = "GET__CHAT_GALLERY_NEXT__SUCCESS",

    REMOVE_EXPIRED_FROM_CHAT_GALLERY = "REMOVE_EXPIRED_FROM_CHAT_GALLERY",

    SET__ACTIVE_CALL = "SET__ACTIVE_CALL",
    CLEAR__ACTIVE_CALL = "CLEAR__ACTIVE_CALL",
    MERGE__ACTIVE_CALL = "MERGE__ACTIVE_CALL",
    RECONCILE__LIVE_CALL = "RECONCILE__LIVE_CALL",
    SET__CALL_MINIMIZED = "SET__CALL_MINIMIZED",
    SET__CALL_ELAPSED_SECONDS = "SET__CALL_ELAPSED_SECONDS",
    SET__ENDED_CALL = "SET__ENDED_CALL",
    CLEAR__ENDED_CALL = "CLEAR__ENDED_CALL",
    SHOW__CALL_TIP = "SHOW__CALL_TIP",
    ADD__CALL_TIP_NOTE = "ADD__CALL_TIP_NOTE",
    CLEAR__CALL_TIP = "CLEAR__CALL_TIP",
  }

  export const setActiveCall = createAction<{
    call: ICallInfo;
    is_incoming: boolean;
  }>(Type.SET__ACTIVE_CALL);
  // Live fields for the call already on screen; never opens one.
  export const mergeActiveCall = createAction<Partial<ICallInfo>>(
    Type.MERGE__ACTIVE_CALL
  );
  export const clearActiveCall = createAction<{ callId: string }>(
    Type.CLEAR__ACTIVE_CALL
  );
  // Settles the call on screen against the call the API says is live, after
  // the live events may have been missed. `askedAboutCallId` is the call that
  // was on screen when the API was asked, so a call that began while the
  // answer was on its way is never judged by an answer that predates it.
  export const reconcileLiveCall = createAction<{
    askedAboutCallId?: string;
    liveCall: ICallInfo | null;
  }>(Type.RECONCILE__LIVE_CALL);
  // Minimizing the call also puts an "Ongoing call" bar above the composer,
  // which lives outside the call overlay, so the state is shared here.
  export const setCallMinimized = createAction<boolean>(
    Type.SET__CALL_MINIMIZED
  );
  export const setCallElapsedSeconds = createAction<number | null>(
    Type.SET__CALL_ELAPSED_SECONDS
  );
  // The finished call, kept until the post-call summary is dismissed.
  export const setEndedCall = createAction<ICallInfo>(Type.SET__ENDED_CALL);
  export const clearEndedCall = createAction(Type.CLEAR__ENDED_CALL);
  // A tip to announce over the call, from the socket or from the tip popup.
  // The same tip raised twice (same id) shows once.
  export const showCallTip = createAction<ICallTipNotice>(Type.SHOW__CALL_TIP);
  // The tip's optional message arrives as a separate message after the tip.
  export const addCallTipNote = createAction<{
    id?: string;
    chat_id?: string;
    note: string;
  }>(Type.ADD__CALL_TIP_NOTE);
  export const clearCallTip = createAction<{ id: string }>(
    Type.CLEAR__CALL_TIP
  );

  export const prepareGetChat = createAction<{ cachedChat?: IChatInfo }>(
    Type.GET__FETCH_CHAT__PREPARE
  );
  export const successGetChat = createAction<IChatInfo>(
    Type.GET__FETCH_CHAT__SUCCESS
  );
  export const resetGetChat = createAction(Type.GET__FETCH_CHAT__RESET);

  export const successGetChatReload = createAction<IChatInfo>(
    Type.GET__FETCH_CHAT_RELOAD__SUCCESS
  );

  export const successPostCreateChat = createAction<IChatInfo>(
    Type.POST__CREATE_CHAT__SUCCESS
  );

  export const updateChatById = createAction<{
    chat_id: string;
    newValues: Partial<IChatInfo>;
  }>(Type.UPDATE__CHAT_BY_ID);

  export const successGetMessages = createAction<{
    otherUserId: string;
    res: MessagePage;
  }>(Type.GET__FETCH_MESSAGES__SUCCESS);
  export const resetGetMessages = createAction(Type.GET__FETCH_MESSAGES__RESET);
  export const successGetNextMessages = createAction<MessagePage>(
    Type.GET__FETCH_NEXT_MESSAGES__SUCCESS
  );

  export const successGetMessageById = createAction<IMessageInfo>(
    Type.GET__MESSAGE_BY_ID__SUCCESS
  );

  export const successGetFromLastIdMessages = createAction<{
    res: { data: IMessageInfo[] };
    config: IGetFromLastIdMessagesConfig;
  }>(Type.GET__FROM_LAST_ID_MESSAGES__SUCCESS);

  export const initPostSendMessage = createAction<{
    clientSideMessage: IMessageInfo;
    __clientSideSendId?: string;
  }>(Type.POST__SEND_MESSAGE__INIT);
  export const successPostSendMessage = createAction<{
    message: IMessageInfo;
    __clientSideSendId: string;
  }>(Type.POST__SEND_MESSAGE__SUCCESS);
  export const failurePostSendMessage = createAction<
    IMessageInfo & { error: { message: string } }
  >(Type.POST__SEND_MESSAGE__FAILURE);

  export const successPatchBuyPaidContent = createAction<IMessageInfo>(
    Type.PATCH__BUY_PAID_CONTENT__SUCCESS
  );

  export const removeClientSideMessage = createAction<{
    __clientSideSendId: string;
  }>(Type.REMOVE__CLIENT_SIDE_MESSAGE);

  export const addClientSideMessageReceived = createAction<{
    clientSideMessage: IMessageInfo;
  }>(Type.ADD_CLIENT_SIDE_MESSAGE_RECEIVED);

  export const resetPostSendMessage = createAction(
    Type.RESET__POST_SEND_MESSAGE
  );

  export const updateMessageRevealMedia = createAction<{ messageId: string }>(
    Type.UPDATE__MESSAGE_REVEAL_MEDIA
  );

  export const updateMessageAsPaid = createAction<{
    messageId: string;
    destructAt: string;
  }>(Type.UPDATE__MESSAGE_AS_PAID);

  export const updateMessageAsHidden = createAction<{
    hiddenMessage: IMessageInfo;
  }>(Type.UPDATE__MESSAGE_AS_HIDDEN);

  export const updateMessageAsDeleted = createAction<{
    deletedMessage: IMessageInfo;
  }>(Type.UPDATE__MESSAGE_AS_DELETED);

  export const updateMessage = createAction<{
    message: IMessageInfo;
  }>(Type.UPDATE__MESSAGE);

  export const updateMessageAsExpired = createAction<{
    messageId: string;
  }>(Type.UPDATE__MESSAGE_AS_EXPIRED);

  export const updateMessagesRead = createAction<{
    config: Pick<
      IUpdateMessagesReadConfig,
      "timestampString" | "messageIds" | "destructibles"
    >;
  }>(Type.UPDATE__MESSAGES_READ);

  export const updateSendMessageProgress = createAction<{
    __clientSideSendId?: string;
    progress: number;
    loaded: number;
    total: number;
  }>(Type.UPDATE__SEND_MESSAGE_PROGRESS);

  export const updateMessageUnlockByPayingUsd = createAction<{
    message: IMessageInfo;
  }>(Type.UPDATE__UNLOCK_BY_PAYING_USD);

  export const successPostChatNote = createAction<ChatNote>(
    Type.POST__CHAT_NOTE__SUCCESS
  );

  export const successPatchChatNote = createAction<ChatNote>(
    Type.PATCH__CHAT_NOTE__SUCCESS
  );

  export const successDeleteChatNote = createAction<{
    id: string;
    response?: unknown;
  }>(Type.DELETE__CHAT_NOTE__SUCCESS);

  export const successDeleteMessage = createAction<{
    id: string;
    response: IMessageInfo;
  }>(Type.DELETE__MESSAGE__SUCCESS);

  export const successPatchHideMessage = createAction<{
    id: string;
    response: IMessageInfo;
  }>(Type.PATCH__HIDE_MESSAGE__SUCCESS);

  export const successPostMessageReaction = createAction<{
    messageId: string;
    reaction: string;
    response?: IMessageInfo;
  }>(Type.POST__MESSAGE_REACTION__SUCCESS);

  export const successDeleteMessageReaction = createAction<{
    messageId: string;
    reaction: string;
    response?: IMessageInfo;
  }>(Type.DELETE__MESSAGE_REACTION__SUCCESS);

  export const setNoteText = createAction<string>(Type.SET__NOTE_TEXT);
  export const setEditingNoteId = createAction<string | null>(
    Type.SET__EDITING_NOTE_ID
  );
  export const setNotesOpen = createAction<boolean>(Type.SET__NOTES_OPEN);

  export const successGetChatGallery = createAction<Partial<MessagePage>>(
    Type.GET__CHAT_GALLERY__SUCCESS
  );

  export const successGetChatGalleryNext = createAction<Partial<MessagePage>>(
    Type.GET__CHAT_GALLERY_NEXT__SUCCESS
  );

  export const removeExpiredFromChatGallery = createAction<{
    messageId: string;
  }>(Type.REMOVE_EXPIRED_FROM_CHAT_GALLERY);
}

export type MessengerActions = Omit<typeof IMessengerActions, "Type">;
